<template>
  <div class="entity provider">
    <sui-form-fields v-if="visibleFields.name || visibleFields.type">
      <EntityInput v-model="entity.name" fieldName="name" v-if="visibleFields.name" />
      <EntityDropdown v-model="entity.type" :options="types" fieldName="type" v-if="visibleFields.type" />
    </sui-form-fields>
    <EntityInput v-model="entity.auth.bucket" fieldName="bucket" ref="bucket" v-if="visibleFields.bucket" />
    <EntityInput v-model="entity.auth.apiToken" fieldName="apiToken" v-if="visibleFields.apiToken" />
    <EntityInput v-model="entity.auth.accessId" fieldName="accessId" v-if="visibleFields.accessId" />
    <EntityInput v-model="entity.auth.secretKey" fieldName="secretKey" v-if="visibleFields.secretKey" />
    <EntityInput v-model="entity.auth.refreshToken" fieldName="refreshToken" v-if="visibleFields.refreshToken" />
    <ProviderConnect v-if="entity.constructor.oauth" />
    <sui-form-fields v-if="visibleFields.endpoint">
      <EntityInput v-model="entity.auth.endpoint" fieldName="endpoint" v-if="visibleFields.endpoint" />
      <ProviderPort v-model="entity.auth.port" fieldName="port" v-if="visibleFields.port" />
      <EntityInput v-model="entity.auth.login" fieldName="login" v-if="visibleFields.login" />
      <EntityInput v-model="entity.auth.password" fieldName="password" v-if="visibleFields.password" />
    </sui-form-fields>
    <sui-form-fields v-if="visibleFields.region || visibleFields.class">
      <EntityDropdown v-model="entity.auth.region" fieldName="region" :options="regionsList" v-if="visibleFields.region" />
      <EntityDropdown v-model="entity.auth.class" fieldName="class" :options="visibleFields.class.enum" v-if="visibleFields.class" />
    </sui-form-fields>
    <ProviderCredentials v-model="entity.auth.credentials" fieldName="credentials" v-if="visibleFields.credentials" />
    <ProviderDrives v-model="entity.auth.drive" fieldName="drive" :drives="drives" v-if="visibleFields.drive" />
    <ProviderAddress v-model="entity.options.gelfAddress" fieldName="address" v-if="visibleFields.address" />
    <sui-form-fields v-if="visibleFields.env || visibleFields.regex">
      <EntityInput v-model="entity.options.env" fieldName="env" v-if="visibleFields.env" />
      <EntityInput v-model="entity.options.regex" fieldName="regex" v-if="visibleFields.regex" />
    </sui-form-fields>
    <sui-form-fields v-if="visibleFields.tag || visibleFields.labels || visibleFields.maxReconnect || visibleFields.reconnectDelay">
      <EntityInput type="number" v-model="entity.options.gelfTcpMaxReconnect" :width="3" :disabled="isTcpOptionsDisabled" fieldName="maxReconnect" v-if="visibleFields.maxReconnect" />
      <EntityInput type="number" v-model="entity.options.gelfTcpReconnectDelay" :width="3" :disabled="isTcpOptionsDisabled" fieldName="reconnectDelay" v-if="visibleFields.reconnectDelay" />
      <EntityInput v-model="entity.options.tag" :width="5" fieldName="tag" v-if="visibleFields.tag" />
      <EntityInput v-model="entity.options.labels" :width="5" fieldName="labels" v-if="visibleFields.labels" />
    </sui-form-fields>
    <sui-form-fields v-if="visibleFields.compressionType || visibleFields.compressionLevel">
      <EntityButtons v-model="entity.options.gelfCompressionType" :disabled="isUdpOptionsDisabled" fieldName="compressionType" v-if="visibleFields.compressionType" />
      <EntityButtons class="numbers" v-model="entity.options.gelfCompressionLevel" :disabled="isUdpOptionsDisabled || entity.options.gelfCompressionType === 'none'" fieldName="compressionLevel" v-if="visibleFields.compressionLevel" />
    </sui-form-fields>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import EntityButtons from '@/components/forms/EntityButtons'
  import EntityInput from '@/components/forms/EntityInput'
  import EntityDropdown from '@/components/forms/EntityDropdown'
  import ProviderAddress from '@/components/forms/ProviderAddress'
  import ProviderConnect from '@/components/forms/ProviderConnect'
  import ProviderCredentials from '@/components/forms/ProviderCredentials'
  import ProviderDrives from '@/components/forms/ProviderDrives'
  import ProviderPort from '@/components/forms/ProviderPort'
  import FormEntity from '@/mixins/FormEntity'
  import objects from '@/objects'

  export default {
    name: 'FormProvider',
    mixins: [
      FormEntity,
    ],
    components: {
      EntityButtons,
      EntityInput,
      EntityDropdown,
      ProviderAddress,
      ProviderConnect,
      ProviderCredentials,
      ProviderDrives,
      ProviderPort,
    },
    data() {
      return {
        object: 'Provider',
        commonFields: ['name', 'type'],
        drives: [],
        isTcpOptionsDisabled: false,
        isUdpOptionsDisabled: false,
      }
    },
    computed: {
      ...mapGetters(['account', 'regions']),
      types() {
        return objects._providers.map(provider => {
          return {
            image: {src: provider._logo},
            text: provider.providerTitle,
            value: provider.providerType,
          }
        })
      },
      regionsList() {
        if (!this.visibleFields.region) {
          return []
        }
        const providerRegions = this.regions.filter(region => this.visibleFields.region.enum.includes(region.id))
        return providerRegions.map(region => {
          return {
            flag: region.country,
            text: region.city,
            value: region.id,
          }
        })
      },
    },
    watch: {
      'entity.type'(newType, oldType) {
        if (oldType) {
          this.createEntity()
          this.$nextTick(() => {
            if (this.entity.auth === null && this.regionsList.length) {
              this.entity.auth.region = this.regionsList[0].value
            }
          })
        }
      },
      'entity.auth.region'() {
        if (this.entity && this.entity.auth && this.entity.auth.region && (!this.entity.auth.bucket || !this.$refs.bucket.changed)) {
          this.entity.auth.bucket = `d2c-${this.account.shortId}-${this.entity.auth.region}`
        }
      },
      'entity.options.gelfAddress': {
        immediate: false,
        handler(newValue = '') {
          if (newValue.includes('tcp://')) {
            this.entity.options.gelfCompressionType = 'none'
            this.entity.options.gelfCompressionLevel = 0
            this.entity.options.gelfTcpMaxReconnect = this.entity.constructor.fields.maxReconnect ? this.entity.constructor.fields.maxReconnect.default : null
            this.entity.options.gelfTcpReconnectDelay = this.entity.constructor.fields.reconnectDelay ? this.entity.constructor.fields.reconnectDelay.default : null
            this.isTcpOptionsDisabled = false
            this.isUdpOptionsDisabled = true
          } else {
            this.entity.options.gelfCompressionType = this.entity.constructor.fields.compressionType ? this.entity.constructor.fields.compressionType.default : null
            this.entity.options.gelfCompressionLevel = this.entity.constructor.fields.compressionLevel ? this.entity.constructor.fields.compressionLevel.default : null
            this.entity.options.gelfTcpMaxReconnect = 0
            this.entity.options.gelfTcpReconnectDelay = 0
            this.isTcpOptionsDisabled = true
            this.isUdpOptionsDisabled = false
          }
        },
      },
    },
  }
</script>

<style scoped>
  .numbers >>> .button { padding: .78571429em!important }
</style>
